import React, { useImperativeHandle, forwardRef, useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Upload } from 'antd';
import kycService from '../../services/onboarding/kycService';
import './Onboarding.css';
import { useOnboarding } from './OnboardingContext';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { handleQualityUploadDestroy } from 'services/qualityService';

const { Dragger } = Upload;

const getValidationSchema = (buyerSellerType) => Yup.object().shape({
  accountNumber: Yup.string()
    .min(9, 'Account number must be at least 9 characters')
    .max(18, 'Account number must not exceed 18 characters')
    .when([], {
      is: () => buyerSellerType !== 'BuyOnly',
      then: schema => schema.required('Account number is required'),
      otherwise: schema => schema.notRequired()
    }),
  ifscCode: Yup.string()
    .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC code format')
    .when([], {
      is: () => buyerSellerType !== 'BuyOnly',
      then: schema => schema.required('IFSC code is required'),
      otherwise: schema => schema.notRequired()
    }),
});

const BankDetails = forwardRef(({ title = 'Bank Details', onComplete, buyerSellerType,isSaveButtonDisable, setIsSaveButtonDisable }, ref) => {
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [errorMessageType, setErrorMessageType] = useState('');
  const [bankInfo, setBankInfo] = useState(null);
  const [fullBankInfo, setFullBankInfo] = useState(null);
  const [validatingField, setValidatingField] = useState(null);
  const [verifiedAccount, setVerifiedAccount] = useState('');
  const [verifiedIfsc, setVerifiedIfsc] = useState('');
  const [showChequeUpload, setShowChequeUpload] = useState(false);
  const [chequeFile, setChequeFile] = useState(null);
  const [chequeFileList, setChequeFileList] = useState([]);
  const {
    updateSection,
    markStepComplete,
    markStepIncomplete,
    getStepData
  } = useOnboarding();

  // Get existing data for this step
  const existingData = getStepData(4); // Step 4 is bank

  useEffect(() => {
    if (buyerSellerType !== 'BuyOnly') {
      setIsSaveButtonDisable(true);
    }
  }, []);

  // Initialize state from existing data
  useEffect(() => {
    if (existingData.verifiedInfo) {
      setBankInfo(existingData.verifiedInfo);
    }
    if (existingData.fullVerifiedInfo) {
      setFullBankInfo(existingData.fullVerifiedInfo);
    }
    // Restore verified account and IFSC if they were previously verified
    if (existingData.accountNumber && existingData.ifscCode) {
      setVerifiedAccount(existingData.accountNumber);
      setVerifiedIfsc(existingData.ifscCode);
    }
    if (existingData.showChequeUpload !== undefined) {
      setShowChequeUpload(existingData.showChequeUpload);
    }
    if (existingData.chequeFile) {
      setChequeFile(existingData.chequeFile);
      setChequeFileList([existingData.chequeFile]);
    }
    // Restore validation errors if bank verification failed previously
    if (existingData.bankDetailsError && !existingData.verifiedInfo) {
      setValidationErrors(prev => ({
        ...prev,
        bankDetails: existingData.bankDetailsError
      }));
      if (existingData.errorMessageType) {
        setErrorMessageType(existingData.errorMessageType);
      }
    }
  }, [existingData]);
  // const { updateSection } = useOnboarding();
  const [chequeAttachmentDetails, setChequeAttachmentDetails] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);

  // Function to clear validation errors when user starts typing
  const clearValidationErrors = () => {
    setValidationErrors({});
    setBankInfo(null);
    setFullBankInfo(null);
    setShowChequeUpload(false);
    // Clear the saved error state from context
    updateSection('bank', {
      ...existingData,
      showChequeUpload: false,
      bankDetailsError: null,
      errorMessageType: null
    });
  };

  // Function to save form data as user types
  const saveFormData = (fieldName, value) => {
    const currentData = {
      ...existingData,
      [fieldName]: value
    };
    updateSection('bank', currentData);
  };

  const handleBankVerification = async (accountNumber, ifscCode) => {
    try {
      setIsLoading(true);
      setValidatingField('bankDetails');
      setValidationErrors(prev => ({ ...prev, bankDetails: null }));
      setBankInfo(null);
      setFullBankInfo(null);
      setShowChequeUpload(false);

      const response = await kycService.verifyDocument(4, accountNumber, ifscCode);

      console.log('Bank verification response:', response);

      // Check if response has the expected structure
      if (response?.response_code === "100" && response?.result) {
        setBankInfo(response?.result);
        setFullBankInfo(response);
        setValidationErrors(prev => ({ ...prev, bankDetails: null }));
        setVerifiedAccount(accountNumber || '');
        setVerifiedIfsc((ifscCode || '').toUpperCase());
        setShowChequeUpload(false);
        // Save successful verification state and clear error state
        updateSection('bank', {
          accountNumber: accountNumber,
          ifscCode: ifscCode,
          verifiedInfo: response?.result,
          fullVerifiedInfo: response,
          showChequeUpload: false,
          bankDetailsError: null,
          errorMessageType: null
        });

        setIsSaveButtonDisable(false);
      } else if (response?.response_code === "107") {
        // Handle duplicate transaction
        const errorMessage = "Duplicate transaction detected. Please try again later.";
        setValidationErrors(prev => ({
          ...prev,
          bankDetails: errorMessage
        }));
        setBankInfo(null);
        setFullBankInfo(null);
        // Save the invalid state to context so it persists when user navigates away
        updateSection('bank', {
          accountNumber: accountNumber,
          ifscCode: ifscCode,
          showChequeUpload: false,
          bankDetailsError: errorMessage
        });
      } else {
        // Handle other failed verification
        // const errorMessage = response?.response_message ||
        //                    response?.metadata?.reason_message ||
        //                    "Invalid bank details";
        const errorMessage = response?.metadata?.reason_code === 'ERR' ? response?.metadata?.reason_message : 'Invalid Account Number';
        setErrorMessageType(response?.metadata?.reason_code)
        console.log("errorMessage", errorMessage)
        setValidationErrors(prev => ({
          ...prev,
          bankDetails: errorMessage
        }));
        setBankInfo(null);
        setFullBankInfo(null);
        setShowChequeUpload(true); // Show cheque upload option for other failures
        // Save the invalid state to context so it persists when user navigates away
        updateSection('bank', {
          accountNumber: accountNumber,
          ifscCode: ifscCode,
          showChequeUpload: true,
          bankDetailsError: errorMessage,
          errorMessageType: response?.metadata?.reason_code
        });
      }
    } catch (error) {
      const errorMessage = error.message || "Error validating bank details";
      setValidationErrors(prev => ({
        ...prev,
        bankDetails: errorMessage
      }));
      setBankInfo(null);
      setFullBankInfo(null);
      setShowChequeUpload(true); // Show cheque upload option for errors
      // Save the invalid state to context so it persists when user navigates away
      updateSection('bank', {
        accountNumber: accountNumber,
        ifscCode: ifscCode,
        showChequeUpload: true,
        bankDetailsError: errorMessage
      });
    } finally {
      setIsLoading(false);
      setValidatingField(null);
    }
  };

const handleChequeUploadcopy = async (event) => {
  const file = event.target.files;
  const fileInfo = event.target.files[0]
  if (!file) return;

    // Prevent duplicate selection of the same file (match by name, size, lastModified if present)
    // console.log("ChequeFile", chequeFile, fileInfo)
    if (
      chequeFile &&
      fileInfo.name === chequeFile.name &&
      fileInfo.size === chequeFile.size &&
      (fileInfo.lastModified ? fileInfo.lastModified === chequeFile.lastModified : true)
    ) {
      setValidationErrors(prev => ({ ...prev, chequeFile: 'This file is already selected.' }));
      return;
    }

  const entity_type_enum = 15
  const front_back_enum = 1
  const entity_id = 6409
  const attachmentIdToPass = 0
  const document_module_enum = 2
  const company_affiliate_enum = 1
  const module_enum = 34

  try {
    const reader = new FileReader();
    reader.onloadend = async () => {
      const binaryData = reader.result;
      const response = await handleImageUploadCancelCheque(
        file,
        entity_type_enum,
        front_back_enum,
        entity_id,
        attachmentIdToPass,
        document_module_enum,
        company_affiliate_enum,
        module_enum
      )
      setChequeAttachmentDetails(response)
      setUploadedImage(fileInfo);
      setValidationErrors(prev => ({ ...prev, chequeFile: null }));
    setChequeFile(fileInfo);
    setChequeFileList([fileInfo]);
    console.log('Cheque selected:', fileInfo);
    };
    reader.readAsArrayBuffer(fileInfo);
  } catch (error) {
    console.error("Image upload failed:", error);
  }
};

const handleChequeUpload = async (event) => {
  const files = event.target.files;
  const fileInfo = files && files[0];
  if (!fileInfo) return;

  // Prevent duplicate selection of the same file (name+size+lastModified)
  if (
    chequeFile &&
    fileInfo.name === chequeFile.name &&
    fileInfo.size === chequeFile.size &&
    (fileInfo.lastModified ? fileInfo.lastModified === chequeFile.lastModified : true)
  ) {
    setValidationErrors(prev => ({ ...prev, chequeFile: 'This file is already selected.' }));
    return;
  }

  try {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result; // data:<mime>;base64,....
      try {
        const payload = {
          name: fileInfo.name,
          size: fileInfo.size,
          type: fileInfo.type,
          lastModified: fileInfo.lastModified || null,
          dataUrl: base64,
        };
        localStorage.setItem('cancelledChequeImage', JSON.stringify(payload));
        console.log('Saved cancelledChequeImage:', payload);
        setValidationErrors(prev => ({ ...prev, chequeFile: null }));
        setChequeFile(fileInfo);
        setChequeFileList([fileInfo]);
        setUploadedImage(fileInfo);
        updateSection('bank', { chequeFile: fileInfo, showChequeUpload: true });
      } catch (e) {
        console.error('Error saving cheque base64 to localStorage:', e);
      }
    };
    reader.readAsDataURL(fileInfo);
  } catch (error) {
    console.error('Cheque selection failed:', error);
  }
};

const handleChequeDelete = (attachment_id) => {
  const deleteCheque = async () => {
    try {
      const resp = await handleQualityUploadDestroy(attachment_id)
      if(resp){
        setChequeAttachmentDetails(null);
        setUploadedImage(null);
      }
    } catch (error) {
      console.log(error)
    }
  }
  deleteCheque()
};

  const handleChequeRemove = () => {
    setChequeFile(null);
    setChequeFileList([]);
    setValidationErrors(prev => ({ ...prev, chequeFile: null }));
    try { localStorage.removeItem('cancelledChequeImage'); } catch (e) {}
  };

  const initialValues = {
    accountNumber: existingData.accountNumber || '',
    ifscCode: existingData.ifscCode || '',
  };

  const handleSubmit = (values, { setSubmitting }) => {
    // Save complete data to context
    const completeData = {
      accountNumber: values.accountNumber,
      ifscCode: values.ifscCode, // Fix: use ifscCode instead of ifsc
      ifsc: values.ifscCode, // Keep this for backward compatibility
      bankVerified: bankInfo,
      fullBankVerified: fullBankInfo,
      verifiedInfo: bankInfo, // Add this for consistency
      fullVerifiedInfo: fullBankInfo, // Add this for consistency
      showChequeUpload,
      chequeFile
    };

    updateSection('bank', completeData);

    // Mark step as complete if bank details are provided and verified
    if (buyerSellerType === 'BuyOnly' || (values.accountNumber && values.ifscCode && bankInfo)) {
      markStepComplete(4);
    } else {
      markStepIncomplete(4);
    }

    onComplete({
      bankDetails: {
        accountNumber: values.accountNumber,
        ifsc: values.ifscCode,
        bankVerified: bankInfo,
        fullBankVerified: fullBankInfo
      }
    });
    setSubmitting(false);
  };

  return (
    <div className="step-form">
      <h2>{title}</h2>

      {/* Account Type Requirements Warning */}
      <div style={{
        background: '#fff3cd',
        border: '1px solid #ffeeba',
        borderRadius: '4px',
        padding: '1rem',
        marginBottom: '1.5rem',
        color: '#856404',
        fontSize: '1rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem'
      }}>
        <svg width="32" height="32" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
          <circle cx="10" cy="10" r="10" fill="#ffc107"/>
          <path d="M10 6V11M10 14H10.01" stroke="#856404" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>
          <b>Important:</b> If you're signing up as a company, please make sure to provide your current account details—it's a must! For all individuals, a savings account is must.
        </span>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={getValidationSchema(buyerSellerType)}
        onSubmit={handleSubmit}
        innerRef={ref}
        enableReinitialize={true}
      >
        {({ values, isSubmitting, touched, errors, setFieldValue, submitForm }) => {
          // useEffect(()=>{
          //   if(!values?.accountNumber || !values?.ifscCode){
          //     setIsSaveButtonDisable(true);
          //   }else{
          //     setIsSaveButtonDisable(false);
          //   }
          // },[values?.accountNumber, values?.ifscCode])
          useEffect(()=>{
if(showChequeUpload && !bankInfo && !chequeFile){
              setIsSaveButtonDisable(true);
            }else{
              setIsSaveButtonDisable(false);
            }
          },[showChequeUpload, bankInfo, chequeFile])
          useImperativeHandle(ref, () => ({ submitForm }));
          return (
            <Form>
              <div className="form-field">
                <label htmlFor="accountNumber">Account Number</label>
                <div style={{ position: 'relative' }}>
                  <Field
                    type="text"
                    name="accountNumber"
                    id="accountNumber"
                    className={`form-input ${touched.accountNumber && (errors.accountNumber || validationErrors.accountNumber) ? 'error' : ''}`}
                    placeholder="Enter your account number"
                    disabled={isLoading}
                    maxLength="18"
                    onChange={(e) => {
                      // Clear validation errors when user starts typing
                      clearValidationErrors();
                      setFieldValue('accountNumber', e.target.value);
                      // Save data as user types
                      saveFormData('accountNumber', e.target.value);
                    }}
                  />
                </div>
                <ErrorMessage name="accountNumber" component="div" className="error-message" />
                {/* {errorMessageType !== 'ERR' && (
                <div className="error-message">{validationErrors.bankDetails}</div>
              )} */}
              </div>

              <div className="form-field">
                <label htmlFor="ifscCode">IFSC Code</label>
                <div style={{ display: 'flex', alignItems: 'stretch', width: '100%' }}>
                  <Field
                    type="text"
                    name="ifscCode"
                    id="ifscCode"
                    className={`form-input ${touched.ifscCode && (errors.ifscCode || validationErrors.ifscCode) ? 'error' : ''}`}
                    placeholder="Enter IFSC code (e.g., SBIN0001234)"
                    disabled={isLoading}
                    style={{ textTransform: 'uppercase', flex: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                    maxLength="11"
                    value={values.ifscCode}
                    onChange={(e) => {
                      // Clear validation errors when user starts typing
                      clearValidationErrors();
                      setFieldValue('ifscCode', e.target.value.toUpperCase());
                      // Save data as user types
                      saveFormData('ifscCode', e.target.value.toUpperCase());
                    }}
                  />
                  <button
                    type="button"
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      background: (isLoading || !values.accountNumber || !values.ifscCode || errors.accountNumber || errors.ifscCode || (bankInfo && values.accountNumber === verifiedAccount && values.ifscCode.toUpperCase() === verifiedIfsc)) ? '#6c757d' : '#1b62ab',
                      color: '#fff',
                      border: `1px solid ${(isLoading || !values.accountNumber || !values.ifscCode || errors.accountNumber || errors.ifscCode || (bankInfo && values.accountNumber === verifiedAccount && values.ifscCode.toUpperCase() === verifiedIfsc)) ? '#6c757d' : '#1b62ab'}`,
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      padding: '0 16px',
                      cursor: (isLoading || !values.accountNumber || !values.ifscCode || errors.accountNumber || errors.ifscCode || (bankInfo && values.accountNumber === verifiedAccount && values.ifscCode.toUpperCase() === verifiedIfsc)) ? 'not-allowed' : 'pointer',
                      opacity: (isLoading || !values.accountNumber || !values.ifscCode || errors.accountNumber || errors.ifscCode || (bankInfo && values.accountNumber === verifiedAccount && values.ifscCode.toUpperCase() === verifiedIfsc)) ? 0.6 : 1,
                      height: '50px',
                      minWidth: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '0px 0.375rem 0.375rem 0px',
                      gap: '8px'
                    }}
                    disabled={isLoading || !values.accountNumber || !values.ifscCode || errors.accountNumber || errors.ifscCode || (bankInfo && values.accountNumber === verifiedAccount && values.ifscCode.toUpperCase() === verifiedIfsc)}
                    onClick={() => handleBankVerification(values.accountNumber, values.ifscCode)}
                  >
                    {isLoading ? (
                      <>
                        <div className="loading-spinner" style={{ width: '16px', height: '16px', border: '2px solid #f3f3f3', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                        Validating...
                      </>
                    ) : (bankInfo && values.accountNumber === verifiedAccount && values.ifscCode.toUpperCase() === verifiedIfsc) ? (
                      'Verified'
                    ) : (
                      'Validate'
                    )}
                  </button>
                </div>
                <ErrorMessage name="ifscCode" component="div" className="error-message" />
                {/* {errorMessageType === 'ERR' && (
                <div className="error-message">{validationErrors.bankDetails}</div>
              )} */}
              </div>



              {/* Verification Result */}
              {bankInfo?.beneficiary_name && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '0.5rem',
                  color: '#28a745'
                }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '0.5rem' }}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="10" fill="#7ED957"/>
                      <path d="M6 10.5L9 13.5L14 8.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <span>Bank Verified - {bankInfo.beneficiary_name}</span>
                </div>
              )}

              {validatingField === 'bankDetails' && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '0.5rem',
                  color: '#757d84'
                }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '0.5rem' }}>
                    <svg width="18" height="18" viewBox="0 0 50 50">
                      <circle cx="25" cy="25" r="20" fill="none" stroke="#7ED957" strokeWidth="5" strokeDasharray="31.4 31.4" strokeLinecap="round">
                        <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="0.8s" from="0 25 25" to="360 25 25"/>
                      </circle>
                    </svg>
                  </span>
                  <span>Verifying bank details...</span>
                </div>
              )}

              {validationErrors.bankDetails && (
                <div className="error-message">{validationErrors.bankDetails}</div>
              )}

                   {/* Cheque Upload Option */}
                   {showChequeUpload && !bankInfo && (
                <>
                  <div style={{ marginTop: 16 }}>
                    <div
                      style={{
                        border: '2px dashed #1B62AB',
                        borderRadius: 8,
                        padding: '20px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        color: '#1B62AB',
                        backgroundColor: '#F5FAFF',
                        width: '100%',
                        // maxWidth: 400,
                        marginTop: 16
                      }}
                    >
                      <label htmlFor="upload-image" style={{ cursor: 'pointer', display: 'block' }}>
                        <div>
                          <UploadOutlined style={{ fontSize: 28, marginBottom: 8 }} />
                        </div>
                        <div style={{ fontWeight: 500 }}>Click to upload cancelled cheque</div>
                        <div style={{ fontSize: 12, color: '#888' }}>Supported formats: JPG, PNG</div>
                      </label>

                      <input
                        type="file"
                        accept="image/*"
                        id="upload-image"
                        onChange={handleChequeUpload}
                        style={{ display: 'none' }}
                      />

                      {chequeFile && (
                        <div style={{ marginTop: 8, color: '#28a745', fontSize: 14 }}>
                          Selected: {chequeFile.name}
                        </div>
                      )}
                      {!chequeFile && (() => {
                        try {
                          const saved = localStorage.getItem('cancelledChequeImage');
                          if (saved) {
                            const p = JSON.parse(saved);
                            if (p?.name) {
                              return (
                                <div style={{ marginTop: 8, color: '#28a745', fontSize: 14 }}>
                                  Selected: {p.name}
                                </div>
                              );
                            }
                          }
                        } catch (e) {}
                        return null;
                      })()}

                      {chequeAttachmentDetails && (
                        <div
                          style={{
                            marginTop: 16,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexWrap: 'wrap',
                            gap: 8,
                            color: '#333',
                          }}
                        >
                          <a href={chequeAttachmentDetails.attachment_path} target="_blank" style={{ fontSize: 14 }}>{chequeAttachmentDetails.attachment_file_name}</a>
                          <DeleteOutlined
                            onClick={()=>{
                              handleChequeDelete(chequeAttachmentDetails.attachment_id)
                              handleChequeRemove()
                            }}
                            style={{ color: '#f44336', cursor: 'pointer', fontSize: 18 }}
                          />
                        </div>
                              )}
                    </div>
                  </div>
                </>
              )}

              <div className="form-field">
                <small className="form-text text-muted">
                  Please ensure your account number and IFSC code are correct. This information will be used for payment processing.
                </small>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
});

export default BankDetails;
